import template from './slidedown.template';

/**
 * Enumerates the possible states of the slidedown panel.
 */
enum PanelState {
    OPEN = 'open',
    CLOSED = 'closed',
}

/**
 * <cbw-slidedown>
 *
 * The refactored component now uses an explicit `state` attribute whose value
 * is either `open` or `closed`.  Internal logic reacts to changes to this
 * single source of truth instead of toggling arbitrary CSS classes directly.
 * For backwards‑compatibility the legacy `panel_open` attribute is still
 * reflected so existing integrations (e.g. the header scripts) keep working.
 *
 * The public API therefore comprises:
 *   • `state` attribute / property – "open" | "closed"
 *   • `open()` / `close()` / `toggle()` convenience methods.
 */
export default class Slidedown extends HTMLElement {
    /** Internal state (SSOT). */
    private panelState: PanelState = PanelState.CLOSED;

    constructor() {
        super();
        this.toggle = this.toggle.bind(this);
        this.close = this.close.bind(this);
        this.open = this.open.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onEscape = this.onEscape.bind(this);
    }

    /* -------------------------- Web‑component life‑cycle ------------------ */

    static get observedAttributes(): string[] {
        // React to external updates to the state *or* the active class name.
        return ['state', 'active-class'];
    }

    connectedCallback(): void {
        // Inject HTML only once.
        if (!this.hasChildNodes()) {
            this.innerHTML = template;
        }

        // Sync initial attributes → internal state.
        this.panelState = (this.getAttribute('state') as PanelState) ?? PanelState.CLOSED;
        this.applyState();
        this.showActiveOnly();

        // Global listeners.
        window.addEventListener('resize', this.onResize);
        document.addEventListener('keydown', this.onEscape);

        // Prevent keyboard tabbing into the inactive panels by making the wrappers invisible when not active
        const navPanel = this.querySelector('.cbw-slidedown-nav-panel');
        const navPanelWrapper = navPanel?.querySelector('.cbw-slidedown-nav-wrapper');
        const searchPanel = this.querySelector('.cbw-slidedown-search-panel');
        const searchWrapper = searchPanel?.querySelector('.cbw-slidedown-search-wrapper');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        navPanelWrapper?.addEventListener('transitionend', (event: any) => {
            if (
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (event as any).propertyName === 'height' &&
                event.target === navPanelWrapper &&
                navPanelWrapper.clientHeight === 0
            ) {
                navPanel?.classList.add('cbw-invisible');
            }
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        searchWrapper?.addEventListener('transitionend', (event: any) => {
            if (
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (event as any).propertyName === 'height' &&
                event.target === searchWrapper &&
                searchWrapper.clientHeight === 0
            ) {
                searchPanel?.classList.add('cbw-invisible');
            }
        });

        const transition = this.querySelector('.cbw-slidedown-search-panel');

        transition?.addEventListener('transitionend', () => {
            if (this.panel_open) {
                transition.setAttribute('style', 'overflow:visible');
            }
        });

        // Notify consumers we are ready.
        this.dispatchEvent(new CustomEvent('slidedown_connected', { bubbles: true, composed: true }));
    }

    disconnectedCallback(): void {
        window.removeEventListener('resize', this.onResize);
        document.removeEventListener('keydown', this.onEscape);
    }

    /* ------------------------------ State API ---------------------------- */

    /** Current open/closed state as a property. */
    get state(): PanelState {
        return this.panelState;
    }

    /** Updates the component state and triggers side‑effects. */
    set state(value: PanelState) {
        if (value === this.panelState) return;
        this.panelState = value;
        this.setAttribute('state', value);
        this.applyState();
    }

    /** Convenience alias for legacy integrations. */
    get panel_open(): boolean {
        return this.panelState === PanelState.OPEN;
    }
    set panel_open(isOpen: boolean) {
        this.state = isOpen ? PanelState.OPEN : PanelState.CLOSED;
    }

    /** Opens the slidedown. */
    open(): void {
        this.state = PanelState.OPEN;
    }

    /** Closes the slidedown. */
    close(): void {
        this.state = PanelState.CLOSED;
    }

    /** Toggles between *open* and *closed*. */
    toggle(): void {
        this.state = this.panelState === PanelState.OPEN ? PanelState.CLOSED : PanelState.OPEN;
    }

    /* ------------------- Attribute → property reflection ------------------ */

    attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
        if (newValue !== oldValue) {
            switch (name) {
                case 'state':
                    this.panelState = (newValue as PanelState) ?? PanelState.CLOSED;
                    // Apply the new state to
                    this.applyState();
                    break;
                case 'active-class':
                    this.showActiveOnly();
                    break;
            }
        }
    }

    /* ----------------------------- Internals ----------------------------- */

    /** Applies `panelState` to the DOM & notifies listeners. */
    private applyState(): void {
        const isOpen = this.panelState === PanelState.OPEN;

        // Reflect legacy attribute for backward compatibility.
        if (isOpen) {
            this.setAttribute('panel_open', '');
        } else {
            this.removeAttribute('panel_open');
        }

        // Dispatch single consolidated event.
        this.dispatchEvent(
            new CustomEvent('slideDownEvent', {
                detail: { isOpen, activeClass: this.getAttribute('active-class') },
                bubbles: true,
                cancelable: false,
                composed: true,
            }),
        );

        const panelSelector = `.${this.getAttribute('active-class')}-panel`;
        const panel = this.querySelector(panelSelector) as HTMLElement | null;
        const focusable = panel ? this.getKeyboardElements(panel) : [];

        // A11y & visibility updates.
        if (isOpen) {
            panel?.classList.remove('cbw-invisible');
            this.setNavHeight();

            // Ensure focus stays inside panel.
            focusable.forEach(el => el.setAttribute('tabindex', '0'));
            focusable[0]?.focus();
            panel?.scroll(0, 0);
            document.body.classList.add('cbw-body-lock');
        } else {
            this.setNavHeight(0);
            focusable[0]?.blur();
            focusable.forEach(el => el.setAttribute('tabindex', '-1'));
            document.body.classList.remove('cbw-body-lock');
            // Hide overflow after closing search panel.
            this.querySelector('.cbw-header-search-panel')?.setAttribute('style', 'overflow:hidden');
        }
        panel?.setAttribute('aria-hidden', (!isOpen).toString());
    }

    /** Calculates and sets appropriate height on the active wrapper. */
    private setNavHeight(height?: number): void {
        const wrapperSelector = `.${this.getAttribute('active-class')}-wrapper`;
        const wrapper = this.querySelector(wrapperSelector) as HTMLElement | null;
        if (!wrapper) return;

        const currentHeight = wrapper.clientHeight;
        if (height === 0 || currentHeight === 0) {
            wrapper.classList.add('transition-height');
        } else {
            wrapper.classList.remove('transition-height');
        }

        // Auto‑calculate if height was not provided.
        if (height === undefined) {
            const contentSelector = `.${this.getAttribute('active-class')}-body`;
            const content = wrapper.querySelector(contentSelector) as HTMLElement | null;
            height = content?.offsetHeight ?? 0;
            if (this.getAttribute('active-class') !== 'cbw-slidedown-login') {
                height += window.innerWidth < 768 ? 35 : 100;
            }
        }
        wrapper.style.height = `${height}px`;
    }

    /** Shows only the currently active panel wrapper. */
    private showActiveOnly(): void {
        const active = this.getAttribute('active-class');
        if (!active) return;

        this.querySelectorAll('.cbw-slidedown-wrapper > div').forEach(panel => {
            const element = panel as HTMLElement;
            if (element.classList.contains(active)) {
                element.classList.remove('cbw-hidden-xs-up');
                this.addAccessibleTabbing(element);
            } else {
                element.classList.add('cbw-hidden-xs-up');
            }
        });
    }

    /* ------------------------ Accessibility helpers ----------------------- */

    private getKeyboardElements(root: HTMLElement): HTMLElement[] {
        return Array.from(root.querySelectorAll<HTMLElement>('a, button, input, select, textarea')).filter(
            el => !el.hasAttribute('aria-hidden') && el.getAttribute('type') !== 'hidden',
        );
    }

    private isMobileGlobalNavigation(activeElement: HTMLElement): boolean {
        if (window.innerWidth <= 768 && activeElement?.classList.contains('cbw-slidedown-nav')) {
            // only proceed if useCbLogoLink is true
            return this.getAttribute('cb-link') === 'true';
        }

        return false;
    }

    private addAccessibleTabbing(root: HTMLElement): void {
        const tabbable = this.getKeyboardElements(root);
        if (!tabbable.length) return;

        const first = tabbable[0];
        const last = tabbable[tabbable.length - 1];

        if (!first.classList.contains('cbw-shift-tab-handler')) {
            first.classList.add('cbw-shift-tab-handler');
            first.addEventListener('keydown', evt => {
                if (evt.key === 'Tab' && (evt as KeyboardEvent).shiftKey) {
                    // GS-10856:for mobile GlobalNavigation we want to set focus to
                    // close not close the panel
                    if (!this.isMobileGlobalNavigation(root)) {
                        evt.preventDefault();
                        this.close();
                    }
                }
            });
        }
        if (!last.classList.contains('cbw-tab-handler')) {
            last.classList.add('cbw-tab-handler');
            last.addEventListener('keydown', evt => {
                if (evt.key === 'Tab' && !(evt as KeyboardEvent).shiftKey) {
                    evt.preventDefault();
                    this.close();
                }
            });
        }
    }

    /* ---------------------------- Event hooks ---------------------------- */

    private onResize(): void {
        if (this.panelState === PanelState.OPEN && this.getAttribute('active-class') === 'cbw-slidedown-nav') {
            this.setNavHeight();
        }
        if (
            this.panelState === PanelState.OPEN &&
            window.innerWidth > 768 &&
            this.getAttribute('active-class') === 'cbw-slidedown-search'
        ) {
            this.close();
        }
    }

    private onEscape(evt: KeyboardEvent): void {
        if (evt.key === 'Escape' && this.panelState === PanelState.OPEN) {
            this.close();
        }
    }
}
